import {CompanyResponse} from '@api'
import colors from '@colors'
import {getStatusText} from '@common'
import {TranslationKey, Country, ContractStatus} from '@enums'
import {Stack, SvgIcon, Tooltip, Typography} from '@mui/material'
import {GridColDef, GridRenderCellParams, GridTreeNodeWithRender} from '@mui/x-data-grid'

import {ReactNode} from 'react'

import {t, TFunction} from 'i18next'

import {UIKit} from '@components'

import {countries, icons} from './consts'
import {
  TrueIcon,
  FalseIcon,
  CountryCellContainer,
  ActionButtonContainer,
  ActionButton,
  UnlinkButton,
  StatusCellInnerWrapper,
  StatusCellOuterWrapper,
  ClickableCell,
  CompanyLabelWrapper,
  CompanyWithFlagWrapper,
} from './style'

/**
 * @param country Country
 * @returns Country icon
 */
export const getCountryFlag = (country: string): JSX.Element => <SvgIcon>{icons[country as Country]}</SvgIcon>

/**
 * @param rows Table rows
 * @returns Table rows with 'no' prop
 */
export const getTableRows = <T extends object>(rows: T[]): T[] =>
  rows.map((item, index) => ({
    no: index + 1,
    ...item,
  }))

/**
 * @param columns Table columns
 * @returns Table columns with disabled column menu
 */
export const getTableColumns = (columns: GridColDef[]): GridColDef[] => [
  ...columns.map(column => ({
    disableColumnMenu: true,
    ...column,
  })),
]

/**
 * @returns Pagination page size options
 */
export const getPageSizes = (): UIKit.DropdownItem[] =>
  [10, 25, 50].map(item => ({
    label: item.toString(),
    value: item,
  }))

/**
 * @param t Translation function
 * @returns Cell with an Active status icon
 */
export const activeCell = (t: TFunction): GridColDef => ({
  field: 'active',
  headerName: t(TranslationKey.Active),
  width: 75,
  type: 'boolean',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderCell: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) =>
    params.value ? <TrueIcon /> : <FalseIcon />,
})

export const statusColorMap: Record<
  ContractStatus | 'Active' | 'Terminated' | 'Cancelled' | 'Registration',
  {text: string; background: string; border: string}
> = {
  [ContractStatus.DRAFT]: colors.status.draft,
  [ContractStatus.QUOTE]: colors.status.quote,
  [ContractStatus.AWAITING_VERIFICATION]: colors.status.awaitingVerification,
  [ContractStatus.SENT_FOR_SIGNING]: colors.status.sentForSigning,
  [ContractStatus.ACTIVE]: colors.status.active,
  [ContractStatus.SIGNED]: colors.status.signed,
  [ContractStatus.CANCELED]: colors.status.canceled,
  Terminated: colors.status.canceled,
  Active: colors.status.signed,
  Cancelled: colors.status.canceled,
  Registration: colors.status.sentForSigning,
}

/**
 * @param t Translation function
 * @param status Contract Status
 * @returns Cell with styled Status display
 */
export const getStatusCell = (t: TFunction, status: ContractStatus): ReactNode => {
  const color = statusColorMap[status] ?? colors.status.default

  return (
    <StatusCellOuterWrapper>
      <StatusCellInnerWrapper color={color.background} border={color.border}>
        <Stack style={{height: 12, width: 12, borderRadius: 99, backgroundColor: color.text}} />
        <Typography fontSize={'0.7rem'} color={color.text}>
          {getStatusText(t, status)}
        </Typography>
      </StatusCellInnerWrapper>
    </StatusCellOuterWrapper>
  )
}

/**
 * @param t Translation function
 * @returns A cell with a country name and flag
 */
export const countryCell = (t: TFunction): GridColDef => ({
  field: 'country',
  headerName: t(TranslationKey.Country),
  width: 180,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderCell: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) => (
    <CountryCellContainer>
      <SvgIcon>{icons[params.value as Country]}</SvgIcon>
      <Typography>{countries(t).find(item => item.isoCode === params.value)?.name}</Typography>
    </CountryCellContainer>
  ),
})

/**
 * @param t Translation function
 * @param onClick On click handler function
 * @returns A cell with a View button
 */
export const viewButtonCell = (t: TFunction, onClick: (url: string) => void): GridColDef => ({
  field: 'id',
  headerName: t(TranslationKey.View),
  width: 100,
  filterable: false,
  hideable: false,
  sortable: false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderCell: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) => (
    <ActionButtonContainer>
      <ActionButton variant="contained" onClick={() => onClick(params.value)}>
        {t(TranslationKey.View)}
      </ActionButton>
    </ActionButtonContainer>
  ),
})

export const renderCompaniesCell = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
) => {
  const companies: CompanyResponse[] = params.row.companies || []
  return (
    <Tooltip
      title={
        companies.length > 1
          ? companies
              .map(item => `${item.name} (${countries(t).find(country => country.isoCode === item.country)?.name})`)
              .join(', ')
          : ''
      }
    >
      <CompanyWithFlagWrapper>
        {companies.length > 0 ? (
          <>
            <CompanyLabelWrapper>
              <Typography>{companies[0].name}</Typography>
              {getCountryFlag(companies[0].country)}
            </CompanyLabelWrapper>
            {companies.length > 1 && <Typography>{`+${companies.length - 1}`}</Typography>}
          </>
        ) : (
          '-'
        )}
      </CompanyWithFlagWrapper>
    </Tooltip>
  )
}

export const renderClickableCell = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
  onClick: () => void,
) => (params.row.id ? <ClickableCell onClick={onClick}>{params.value}</ClickableCell> : <Stack>{params.value}</Stack>)

/**
 * @param t Translation function
 * @param onClick On click handler function
 * @returns A cell with an Unlink button
 */
export const unlinkButtonCell = (t: TFunction, onClick: (id: string) => void): GridColDef => ({
  field: 'unlinkId',
  headerName: t(TranslationKey.Unlink),
  width: 100,
  filterable: false,
  hideable: false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderCell: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) => (
    <ActionButtonContainer>
      <UnlinkButton
        variant="contained"
        onClick={() => {
          onClick(params.value)
        }}
      >
        {t(TranslationKey.Unlink)}
      </UnlinkButton>
    </ActionButtonContainer>
  ),
})
